import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Button,
  Chip,
  Stack,
  Paper,
  useTheme,
  useMediaQuery,
  IconButton,
  Dialog,
  DialogContent,
  Grid,
  Skeleton,
  Tooltip,
  Badge,
  CircularProgress,
  DialogTitle,
  DialogActions,
} from '@mui/material';
import {
  Edit as EditIcon,
  PictureAsPdf as PdfIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  PhotoCamera as PhotoIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  LocationOn as LocationIcon,
  BrokenImage as BrokenImageIcon,
  ZoomIn as ZoomInIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { regieReportsAPI } from '../../services/api';
import { useSnackbar } from 'notistack';
import { api } from '../../services/api';
import { API_BASE_URL } from '../../config';

// Helper function to construct photo URL
const getPhotoUrl = (photo: any): string => {
  if (!photo || !photo.file_path) return '';
  
  // Remove leading 'static/' if present since it's included in the API path
  const path = photo.file_path.replace(/^static\//, '');
  
  // Construct the full URL - note we need to go up one level since photos are served from /static
  return `${API_BASE_URL.replace(/\/api$/, '')}/static/${path}`;
};

const RegieReportDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(-1);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);

  // Rollen-basierte Berechtigungen für Regieberichte
  const isAdmin = user?.role === 'admin';
  const isManagement = user?.role === 'management';
  const isBauleiter = user?.role === 'bauleiter';
  const isForeman = user?.role === 'foreman';
  const isInstaller = user?.role === 'installer';
  
  // Admin, Management & Bauleiter können alles
  const canSeeAll = isAdmin || isManagement || isBauleiter;
  // Vorarbeiter & Monteure haben eingeschränkte Rechte
  const canSeeRestricted = isForeman || isInstaller;
  
  // Regieberichte-Berechtigungen (wie Tagesberichte):
  // - Export: Nur Admin, Management & Bauleiter
  // - Bearbeiten: Admin/Management/Bauleiter können alles, Monteure/Vorarbeiter nur eigene Entwürfe
  const canExportReports = canSeeAll;
  const canEditAllReports = canSeeAll;
  
  // 🔒 Individuelle Berechtigung pro Bericht (wie bei Tagesberichten)
  const canEditReport = (report: any | null) => {
    if (!report) return false;
    if (canEditAllReports) return true; // Admin, Management & Bauleiter können alle bearbeiten
    
    if (canSeeRestricted) { // Foreman & Installer
      const isOwnReport = report.created_by === user?.id;
      const isDraft = report.status === 'entwurf';
      return isOwnReport && isDraft; // Muss eigener Bericht UND Entwurf sein
    }
    return false;
  };

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const data = await regieReportsAPI.getReport(Number(id));
        setReport(data);
      } catch (error) {
        console.error('Error fetching report:', error);
        enqueueSnackbar('Fehler beim Laden des Regieberichts', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchReport();
  }, [id, enqueueSnackbar]);

  const buildPdfFilename = () => {
    const siteRaw = (report?.ort || report?.bauvorhaben || report?.auftraggeber_name || 'Unbekannt')
      .replace(/[^\wÄÖÜäöüß\-]+/gi, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
    const d = report?.date ? new Date(report.date) : new Date();
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `Regiebericht_${siteRaw || 'Unbekannt'}_${dd}.${mm}.${yy}.pdf`;
  };

  const handlePdfDownload = async () => {
    if (!id) return;
    setPdfLoading(true);
    try {
      const response = await api.get(
        `/regie-reports/${id}/export-pdf`,
        {
          responseType: 'blob',
        }
      );
      const pdfBlob = new Blob([response.data as BlobPart], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers?.['content-disposition'] as string | undefined;
      const headerMatch = disposition?.match(/filename\*?=(?:UTF-8''|")?([^\";]+)/i);
      const filename = headerMatch
        ? decodeURIComponent(headerMatch[1].replace(/"/g, ''))
        : buildPdfFilename();
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      enqueueSnackbar('PDF erfolgreich heruntergeladen', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Fehler beim PDF-Download', { variant: 'error' });
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePhotoClick = (index: number) => {
    setSelectedPhotoIndex(index);
    setShowPhotoDialog(true);
  };

  const handleClosePhotoDialog = () => {
    setShowPhotoDialog(false);
  };

  const handlePrevPhoto = () => {
    setSelectedPhotoIndex((prev) => 
      prev > 0 ? prev - 1 : (report.photos?.length || 1) - 1
    );
  };

  const handleNextPhoto = () => {
    setSelectedPhotoIndex((prev) => 
      prev < (report.photos?.length || 1) - 1 ? prev + 1 : 0
    );
  };

  if (loading) {
    return (
      <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 2 }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Skeleton variant="text" width={300} height={40} />
        </Box>
        <Box
          display="grid"
          gap={3}
          gridTemplateColumns={{
            xs: '1fr',
            md: '2fr 1fr'
          }}
        >
          <Box>
            <Card>
              <CardContent>
                <Skeleton variant="text" width="60%" height={32} />
                <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
              </CardContent>
            </Card>
          </Box>
          <Box>
            <Card>
              <CardContent>
                <Skeleton variant="text" width="40%" height={32} />
                <Skeleton variant="rectangular" height={300} sx={{ mt: 2 }} />
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
    );
  }

  if (!report) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        minHeight="50vh"
        gap={2}
      >
        <DescriptionIcon color="error" sx={{ fontSize: 48 }} />
        <Typography variant="h6" color="text.secondary">
          Regiebericht nicht gefunden
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/regie-reports')}
        >
          Zurück zur Übersicht
        </Button>
      </Box>
    );
  }

  const InfoRow = ({ label, value, icon }: { label: string; value: string | number | null; icon?: React.ReactNode }) => (
    <Box sx={{ py: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        {icon}
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {label}
        </Typography>
      </Stack>
      <Typography variant="body1" sx={{ pl: icon ? 4 : 0 }}>
        {value || '-'}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tooltip title="Zurück zur Übersicht">
            <IconButton 
              onClick={() => navigate('/regie-reports')} 
              sx={{ 
                mr: 1,
                bgcolor: theme.palette.grey[100],
                '&:hover': {
                  bgcolor: theme.palette.grey[200],
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h4" component="h1">
            Regiebericht #{report.rapport_nr || report.id}
          </Typography>
        </Box>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
          {canExportReports && (
            <Button
              variant="outlined"
              startIcon={<PdfIcon />}
              onClick={handlePdfDownload}
              disabled={pdfLoading}
              sx={{
                minWidth: isMobile ? '100%' : 'auto',
              }}
            >
              {pdfLoading ? 'Wird heruntergeladen...' : 'PDF Export'}
            </Button>
          )}
          {canEditReport(report) && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/regie-reports/${id}/edit`)}
              sx={{
                minWidth: isMobile ? '100%' : 'auto',
              }}
            >
              Bearbeiten
            </Button>
          )}
        </Stack>
      </Box>

      <Box
        display="grid"
        gap={3}
        gridTemplateColumns={{
          xs: '1fr',
          md: '2fr 1fr'
        }}
      >
        {/* Hauptinformationen */}
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DescriptionIcon />
                Projektdetails
              </Typography>
              <Box
                display="grid"
                gap={3}
                gridTemplateColumns={{
                  xs: '1fr',
                  sm: '1fr 1fr'
                }}
              >
                <Box>
                  <InfoRow 
                    label="Projekt" 
                    value={report.project_name}
                    icon={<LocationIcon fontSize="small" color="primary" />}
                  />
                  <InfoRow 
                    label="Datum" 
                    value={format(new Date(report.date), 'dd.MM.yyyy', { locale: de })}
                    icon={<TimeIcon fontSize="small" color="primary" />}
                  />
                  <InfoRow 
                    label="Auftraggeber" 
                    value={report.auftraggeber_name}
                    icon={<PersonIcon fontSize="small" color="primary" />}
                  />
                </Box>
                <Box>
                  <InfoRow 
                    label="Rapport-Nr." 
                    value={report.rapport_nr}
                    icon={<DescriptionIcon fontSize="small" color="primary" />}
                  />
                  <InfoRow 
                    label="Adresse" 
                    value={report.auftraggeber_adresse}
                    icon={<LocationIcon fontSize="small" color="primary" />}
                  />
                  <InfoRow 
                    label="Bauvorhaben" 
                    value={report.bauvorhaben}
                    icon={<DescriptionIcon fontSize="small" color="primary" />}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DescriptionIcon />
                Beauftragung
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Beschreibung
                </Typography>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    backgroundColor: theme.palette.grey[50],
                    minHeight: '100px',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  <Typography variant="body1">
                    {report.ausgefuehrte_arbeiten || report.beauftragung || '-'}
                  </Typography>
                </Paper>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {['Stundenlohn', 'Leistung', 'Pauschal'].map((type) => (
                  <Chip
                    key={type}
                    label={type}
                    color={report.beauftragung_type === type.toLowerCase() ? 'primary' : 'default'}
                    variant={report.beauftragung_type === type.toLowerCase() ? 'filled' : 'outlined'}
                    sx={{
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                      },
                    }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* Mitarbeiter */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon />
                Mitarbeiter
              </Typography>
              <Box
                display="grid"
                gap={2}
                gridTemplateColumns={{
                  xs: '1fr',
                  sm: '1fr 1fr'
                }}
              >
                {report.mitarbeiter?.map((mitarbeiter: any, index: number) => (
                  <Box key={index}>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: theme.shadows[2],
                        },
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon fontSize="small" color="primary" />
                        {mitarbeiter.name}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          <TimeIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                          Stunden: {mitarbeiter.stunden}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Material: {mitarbeiter.material || '-'}
                        </Typography>
                      </Box>
                      {mitarbeiter.maschinen && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Maschinen: {mitarbeiter.maschinen}
                        </Typography>
                      )}
                    </Paper>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Fotos */}
        <Box>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Badge 
                  badgeContent={report.photos?.length || 0} 
                  color="primary"
                  sx={{ mr: 1 }}
                >
                  <PhotoIcon />
                </Badge>
                <Typography variant="h6">
                  Fotos
                </Typography>
              </Box>
              <Box
                display="grid"
                gap={2}
                gridTemplateColumns={{
                  xs: '1fr',
                  sm: '1fr 1fr'
                }}
                sx={{ 
                  '& > *': {
                    aspectRatio: '4/3',
                    height: 'auto',
                  }
                }}
              >
                {report.photos?.map((photo: any, index: number) => (
                  <Box 
                    key={index}
                    sx={{
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: 1,
                      boxShadow: theme.shadows[2],
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.02)',
                        boxShadow: theme.shadows[4],
                        '& .photo-overlay': {
                          opacity: 1,
                        },
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={getPhotoUrl(photo)}
                      alt={`Foto ${index + 1}`}
                      loading="lazy"
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.classList.remove('hidden');
                      }}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        cursor: 'pointer',
                      }}
                      onClick={() => handlePhotoClick(index)}
                    />
                    <Box
                      className="hidden"
                      sx={{
                        display: 'none',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        bgcolor: theme.palette.grey[100],
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: 1,
                      }}
                    >
                      <BrokenImageIcon color="error" sx={{ fontSize: 40 }} />
                      <Typography variant="caption" color="text.secondary">
                        Bild konnte nicht geladen werden
                      </Typography>
                    </Box>
                    <Box
                      className="photo-overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(0, 0, 0, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.2s ease-in-out',
                      }}
                    >
                      <PhotoIcon sx={{ color: 'white', fontSize: 32 }} />
                    </Box>
                  </Box>
                ))}
                {(!report.photos || report.photos.length === 0) && (
                  <Box
                    sx={{
                      gridColumn: '1 / -1',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 3,
                      border: `1px dashed ${theme.palette.divider}`,
                      borderRadius: 1,
                      bgcolor: theme.palette.grey[50],
                    }}
                  >
                    <PhotoIcon sx={{ fontSize: 48, color: theme.palette.grey[400], mb: 1 }} />
                    <Typography color="text.secondary">
                      Keine Fotos vorhanden
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Bildvorschau Dialog */}
      <Dialog
        open={showPhotoDialog}
        onClose={handleClosePhotoDialog}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography>Foto {selectedPhotoIndex + 1} von {report.photos?.length}</Typography>
          <IconButton
            onClick={handleClosePhotoDialog}
            sx={{
              color: 'grey.500',
              '&:hover': {
                color: 'grey.700',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, position: 'relative', height: '80vh' }}>
          {selectedPhotoIndex >= 0 && report.photos && (
            <Box
              component="img"
              src={getPhotoUrl(report.photos[selectedPhotoIndex])}
              alt={`Foto ${selectedPhotoIndex + 1}`}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          )}
          <IconButton
            onClick={handlePrevPhoto}
            sx={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              },
            }}
          >
            <PrevIcon />
          </IconButton>
          <IconButton
            onClick={handleNextPhoto}
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              },
            }}
          >
            <NextIcon />
          </IconButton>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default RegieReportDetail; 